import React from 'react';
import { CheckIcon, WrenchScrewdriverIcon, MapPinIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import CountryCitySelect from '../CountryCitySelect';

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans',       label: 'Diet / Meal Plans' },
    { key: 'sports_massage',        label: 'Sports Massage' },
    { key: 'consultations',         label: 'Consultations' },
    { key: 'online_coaching',       label: 'Online Coaching' },
    { key: 'group_sessions',        label: 'Group Sessions' },
    { key: 'nutrition_advice',      label: 'Nutrition Advice' },
    { key: 'body_composition',      label: 'Body Composition Testing' },
    { key: 'rehabilitation',        label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal',        label: 'Pre / Post Natal' },
    { key: 'mindset_coaching',      label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning', label: 'Strength & Conditioning' },
    { key: 'youth_training',        label: 'Youth / Junior Training' },
];

/**
 * PT Directory profile step — city, postcode and services.
 * Stores into formData.city, formData.postcode, formData.services (comma-separated string).
 */
const PtDirectoryProfileStep = ({ formData, setFormData }) => {

    const selectedServices = formData.services
        ? formData.services.split(',').map(s => s.trim()).filter(Boolean)
        : [];

    const toggleService = (key) => {
        const next = selectedServices.includes(key)
            ? selectedServices.filter(s => s !== key)
            : [...selectedServices, key];
        setFormData({ ...formData, services: next.join(',') });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h3 className="text-lg font-medium text-white mb-1">Your PT Profile</h3>
                <p className="text-gray-400 text-sm">
                    This helps clients find you. You can update everything from your dashboard later.
                </p>
            </div>

            {/* Business / trading name */}
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-gray-300">Business / Trading Name</span>
                    <span className="text-xs text-gray-500">(optional)</span>
                </div>
                <input
                    type="text"
                    value={formData.businessName || ''}
                    onChange={e => setFormData({ ...formData, businessName: e.target.value })}
                    className="input-field"
                    placeholder="e.g. Peak Performance PT — leave blank to use your name"
                />
                <p className="mt-1 text-xs text-gray-500">
                    If set, your business name appears in the directory and forms your profile URL instead of your personal name.
                </p>
            </div>

            {/* Location */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <MapPinIcon className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-gray-300">Where are you based?</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                        <CountryCitySelect
                            country={formData.country || ''}
                            city={formData.city || ''}
                            onCountryChange={val => setFormData({ ...formData, country: val })}
                            onCityChange={val => setFormData({ ...formData, city: val })}
                            cityLabel="City *"
                            cityRequired
                            fieldClass="input-field"
                            labelClass="block text-sm font-medium text-gray-400 mb-1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Postcode</label>
                        <input
                            type="text"
                            value={formData.postcode || ''}
                            onChange={e => setFormData({ ...formData, postcode: e.target.value })}
                            className="input-field"
                            placeholder="e.g. SW1A 1AA"
                        />
                    </div>
                </div>
            </div>

            {/* Services */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <WrenchScrewdriverIcon className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-gray-300">Which services do you offer?</span>
                    <span className="text-xs text-gray-500">(select all that apply)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {SERVICE_OPTIONS.map(({ key, label }) => {
                        const active = selectedServices.includes(key);
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => toggleService(key)}
                                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                    active
                                        ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300'
                                        : 'border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-300'
                                }`}
                            >
                                {active && <CheckIcon className="h-3 w-3" />}
                                {label}
                            </button>
                        );
                    })}
                </div>
                {selectedServices.length === 0 && (
                    <p className="mt-2 text-xs text-gray-500">No services selected — you can add these from your dashboard.</p>
                )}
            </div>
        </div>
    );
};

export default PtDirectoryProfileStep;



